import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, startOfDay, endOfDay } from 'date-fns';
import { getInTransitShipments, getInTransitAlerts, getInTransitTabs, getCustomerCodes, CustomerOption, getGeocodingErrorsCount, OrgLevelKey } from '../api/shipments';
import { useOrgFilter, writeOrgSelection } from '../hooks/useOrgFilter';
import { DataTable } from 'components/ui/data-table';
import { AlertCard, AlertFilter } from 'components/ui/alert-card';
import { createColumns } from './columns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { GeocodingErrorsModal } from './GeocodingErrorsModal';

interface DateRange {
  from?: Date;
  to?: Date;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface SortingState {
  id: string;
  desc: boolean
}


// Helper to safely parse dates from URL (simple YYYY-MM-DD format)
const safeParseDate = (dateString: string | null): Date | undefined => {
  if (dateString) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      // Set to start of day for accurate representation when reading from URL
      return startOfDay(date);
    }
  }
  return undefined;
};

// Helper to initialize Alert Filters from URL
const initializeAlertFilters = (params: URLSearchParams): AlertFilter => {
  const filters: AlertFilter = {};
  const trackingIssue = params.get('tracking_issue');
  if (trackingIssue) {
    filters.tracking_issue = trackingIssue;
  }
  const arrival = params.get('arrival');
  if (arrival) {
    filters.arrival = arrival;
  }
  return filters;
};

// Shown until the first /in-transit/alerts response (same titles, labels and colours as
// the cards the API returns), with the numbers as loading placeholders
const PLACEHOLDER_ALERT_CARDS: AlertCard[] = [
  {
    id: 'action_required', title: 'Action Required', type: 'bucketed', style: 'negative', count: 0,
    buckets: ['Running Late', 'Update Status', 'Delayed'].map((label) => ({ label, count: 0, filters: {} })),
  },
  {
    id: 'tracking_issues', title: 'Tracking Issues', type: 'bucketed', style: 'total', count: 0,
    buckets: ['Missing LHC', 'Missing Pings', 'FASTag vs SIM', 'Distance Increasing'].map((label) => ({ label, count: 0, filters: {} })),
  },
  {
    id: 'arrival', title: 'Arriving Soon', type: 'bucketed', style: 'positive', count: 0,
    buckets: ['< 20 km', '20–50 km', '50–100 km'].map((label) => ({ label, count: 0, filters: {} })),
  },
];

const InTransitShipmentsPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [shipments, setShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(true);

  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [alertCards, setAlertCards] = useState<AlertCard[]>(PLACEHOLDER_ALERT_CARDS);
  const [shipmentStatusCounts, setShipmentStatusCounts] = useState<Record<string, number>>({});
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Load type filter (undefined = all load types) and per-load-type counts for its dropdown
  const [loadType, setLoadType] = useState<string | undefined>(searchParams.get('load_type') || undefined);
  const [loadTypeCounts, setLoadTypeCounts] = useState<Record<string, number>>({});

  // Org hierarchy filter (Division / Zone / Cluster / Controlling Branch / Branch-Project)
  const orgFilter = useOrgFilter('in_transit');
  const { orgParams } = orgFilter;
  // Org selection of the last customer-list load, to spot org changes
  const lastCustomerOrgKey = useRef<string | null>(null);
  // Org selection of the last alerts load: card numbers show placeholders on first load and org changes
  const lastAlertsOrgKey = useRef<string | null>(null);

  // Geocoding errors state
  const [geocodingErrorsCount, setGeocodingErrorsCount] = useState(0);
  const [showGeocodingErrorsModal, setShowGeocodingErrorsModal] = useState(false);

  // Shipment status filter
  const [shipmentStatus, setShipmentStatus] = useState<string | undefined>(searchParams.get('shipment_status') || undefined);


  // Pagination
  const initialPageIndex = parseInt(searchParams.get('page') || '1', 10) - 1;
  const initialPageSize = parseInt(searchParams.get('per_page') || '20', 10);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: Math.max(0, initialPageIndex),
    pageSize: initialPageSize > 0 ? initialPageSize : 20,
  });

  // Sorting
  const initialSortId = searchParams.get('sort_by') || 'cn_date';
  const initialSortOrder = searchParams.get('sort_order') === 'desc';
  const [sorting, setSorting] = useState<SortingState[]>([{ id: initialSortId, desc: initialSortOrder }]);

  // Global Filter (Search)
  const [globalFilter, setGlobalFilter] = useState(searchParams.get('search') || '');

  // Customer Filter
  const [selectedCustId, setSelectedCustId] = useState<string | undefined>(searchParams.get('customer') || undefined);

  // Date Range Filter
  const [dateRange, setDateRange] = useState<DateRange>({
    from: safeParseDate(searchParams.get('from_date')),
    to: safeParseDate(searchParams.get('to_date'))
  });

  // Alert Filters
  const [activeAlertFilters, setActiveAlertFilters] = useState<AlertFilter>(() => initializeAlertFilters(searchParams));

  // Set breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { label: 'Shipments', href: '#' },
      { label: 'In Transit' }
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    // This effect runs whenever a state changes and writes ALL current state to the URL.
    const next = new URLSearchParams();

    // 1. Pagination (pageIndex is 0-based, URL 'page' is 1-based)
    next.set('page', String(pagination.pageIndex + 1));
    next.set('per_page', String(pagination.pageSize));

    // 2. Sorting
    if (sorting.length > 0) {
      next.set('sort_by', sorting[0].id);
      next.set('sort_order', sorting[0].desc ? 'desc' : 'asc');
    }

    // 3. Global Filter (Search)
    if (globalFilter) {
      next.set('search', globalFilter);
    }

    // 4. Customer Filter
    if (selectedCustId) {
      next.set('customer', selectedCustId);
    }

    // 4.5. Org hierarchy filter
    writeOrgSelection(next, orgParams);

    // 5. Date Range Filter
    if (dateRange.from) {
      // Store as simple date string for URL
      next.set('from_date', format(dateRange.from, 'yyyy-MM-dd'));
    }
    if (dateRange.to) {
      // Store as simple date string for URL
      next.set('to_date', format(dateRange.to, 'yyyy-MM-dd'));
    }

    // 6. Active Alert Filters
    if (activeAlertFilters.tracking_issue) {
      next.set('tracking_issue', activeAlertFilters.tracking_issue);
    }
    if (activeAlertFilters.arrival) {
      next.set('arrival', activeAlertFilters.arrival);
    }

    // 7. Load Type
    if (loadType) {
      next.set('load_type', loadType);
    }

    // 8. Shipment Status Filter
    if (shipmentStatus) {
      next.set('shipment_status', shipmentStatus);
    }

    // Update the URL without adding a new entry to the browser history
    setSearchParams(next, { replace: true });

  }, [
    pagination.pageIndex, pagination.pageSize,
    globalFilter,
    selectedCustId,
    orgParams,
    dateRange.from, dateRange.to,
    activeAlertFilters.tracking_issue, activeAlertFilters.arrival,
    sorting[0]?.id, sorting[0]?.desc,
    loadType,
    shipmentStatus,
    setSearchParams
  ]);

  const fetchCustomerData = useCallback(async () => {
    const orgKey = JSON.stringify(orgParams);
    const orgChanged = lastCustomerOrgKey.current !== null && lastCustomerOrgKey.current !== orgKey;
    lastCustomerOrgKey.current = orgKey;
    try {
      const params: any = {};

      if (globalFilter) {
        params.search = globalFilter;
      }
      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }

      params.status = "in_transit";
      Object.assign(params, orgParams);

      const response = await getCustomerCodes(params);

      if (response && response.length > 0) {
        const options: CustomerOption[] = response
          .filter(item => item?.cust_code)
          .map(item => ({
            cust_code: item.cust_code,
            cust_name: item.cust_name || item.cust_code,
          }))
          .sort((a, b) => a.cust_name.localeCompare(b.cust_name));

        setCustomerOptions(options);
        // After an org change, drop a selected customer that has no CNs under the new org
        if (orgChanged) {
          setSelectedCustId((prev) => (prev && !options.some((o) => o.cust_code === prev) ? undefined : prev));
        }
      } else {
        setCustomerOptions([]);
        if (orgChanged) setSelectedCustId(undefined);
      }

    } catch (error) {
      console.error("Error fetching customer data for dropdown:", error);
      setCustomerOptions([]);
    }
  }, [globalFilter, dateRange, orgParams]);

  // Function to fetch geocoding errors count
  const fetchGeocodingErrorsCount = useCallback(async () => {
    try {
      const response = await getGeocodingErrorsCount();
      setGeocodingErrorsCount(response.count);
    } catch (error) {
      console.error('Error fetching geocoding errors count:', error);
      setGeocodingErrorsCount(0);
    }
  }, []);

  // Counts per load type for the Load Type dropdown (every other filter applies)
  const fetchLoadTypeCounts = useCallback(async () => {
    try {
      const params: any = { ...orgParams };

      if (globalFilter) {
        params.search = globalFilter;
      }
      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }
      if (selectedCustId) {
        params.cust_id = selectedCustId;
      }
      if (activeAlertFilters.tracking_issue) {
        params.tracking_issue = activeAlertFilters.tracking_issue;
      }
      if (activeAlertFilters.arrival) {
        params.arrival = activeAlertFilters.arrival;
      }
      if (shipmentStatus) {
        params.shipment_status = shipmentStatus;
      }

      const response = await getInTransitTabs(params);
      setLoadTypeCounts(Object.fromEntries((response.tabs || []).map((tab) => [tab.id, tab.count])));
    } catch (error) {
      console.error('Error fetching load type counts:', error);
      setLoadTypeCounts({});
    }
  }, [globalFilter, dateRange, selectedCustId, orgParams, activeAlertFilters, shipmentStatus]);

  // Function to fetch alert cards
  const fetchAlerts = useCallback(async () => {
    const orgKey = JSON.stringify(orgParams);
    if (lastAlertsOrgKey.current !== orgKey) setIsLoadingAlerts(true);
    lastAlertsOrgKey.current = orgKey;
    try {
      const params: any = { ...orgParams };

      if (globalFilter) {
        params["search"] = globalFilter;
      }
      if (dateRange.from) {
        params["from"] = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params["to"] = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }
      if (selectedCustId) {
        params.cust_id = selectedCustId;
      }
      if (loadType) {
        params.load_type = loadType;
      }
      if (shipmentStatus) {
        params.shipment_status = shipmentStatus;
      }
      if (activeAlertFilters.tracking_issue) {
        params.tracking_issue = activeAlertFilters.tracking_issue;
      }
      if (activeAlertFilters.arrival) {
        params.arrival = activeAlertFilters.arrival;
      }

      const response = await getInTransitAlerts(params);
      setAlertCards(response.cards || []);
      setShipmentStatusCounts(response.status_counts || {});
    } catch (error) {
      console.error('Error fetching alerts:', error);
      setAlertCards([]);
      setShipmentStatusCounts({});
    } finally {
      setIsLoadingAlerts(false);
    }
  }, [globalFilter, dateRange, selectedCustId, orgParams, loadType, shipmentStatus, activeAlertFilters]);

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        search: globalFilter,
        sort_by: sorting[0]?.id || 'cn_date',
        sort_order: sorting[0]?.desc ? 'desc' : 'asc',
        ...orgParams,
      };

      // Add date range to params if provided (local time, start/end of day)
      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }
      if (activeAlertFilters.tracking_issue) {
        params.tracking_issue = activeAlertFilters.tracking_issue;
      }
      if (activeAlertFilters.arrival) {
        params.arrival = activeAlertFilters.arrival;
      }
      if (selectedCustId) {
        params.cust_id = selectedCustId;
      }
      if (loadType) {
        params.load_type = loadType;
      }
      if (shipmentStatus) {
        params.shipment_status = shipmentStatus;
      }

      const response = await getInTransitShipments(params);

      setShipments(response.items || []);
      setTotalCount(response.total_items || 0);
      setPageCount(response.total_pages || 0);
      setError("");
    } catch (error: any) {
      console.error('Error fetching shipments:', error);
      setShipments([]);
      setTotalCount(0);
      setError(error?.response?.data?.detail || error?.message || "Failed to fetch shipments");
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, sorting, dateRange, activeAlertFilters, selectedCustId, orgParams, loadType, shipmentStatus]);

  // Debounced search effect - only reset pagination when search actually changes
  const previousGlobalFilter = useRef('');
  useEffect(() => {
    // Only reset pagination if the search term actually changed (not just initialization)
    if (previousGlobalFilter.current === globalFilter) {
      return;
    }

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, 300);

    setSearchDebounceTimer(timer);
    previousGlobalFilter.current = globalFilter;

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [globalFilter, searchDebounceTimer]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    fetchCustomerData();
  }, [fetchCustomerData]);

  // Fetch geocoding errors count on mount
  useEffect(() => {
    fetchGeocodingErrorsCount();
  }, [fetchGeocodingErrorsCount]);

  // Fetch load type counts when dependencies change
  useEffect(() => {
    fetchLoadTypeCounts();
  }, [fetchLoadTypeCounts]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);


  const handlePaginationChange = (updater: any) => {
    setPagination(updater);
  };

  const handleSortingChange = (updater: any) => {
    setSorting(updater);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
  };

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange || { from: undefined, to: undefined });
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleAlertFilterChange = (filters: AlertFilter) => {
    setActiveAlertFilters(filters);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleCustomerChange = (code: string | undefined) => {
    setSelectedCustId(code);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleOrgLevelChange = (key: OrgLevelKey, value: string | undefined) => {
    orgFilter.setLevel(key, value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleGeocodingErrorsSuccess = () => {
    fetchGeocodingErrorsCount();
  };

  const handleLoadTypeChange = (value: string | undefined) => {
    setLoadType(value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleShipmentStatusChange = (value: string | undefined) => {
    setShipmentStatus(value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const columnsWithRefresh = createColumns(fetchShipments, fetchAlerts);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0">
        <DataTable
          columns={columnsWithRefresh}
          data={shipments}
          loading={loading}
          error={error}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          globalFilter={globalFilter}
          onGlobalFilterChange={handleGlobalFilterChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          totalCount={totalCount}
          pageCount={pageCount}
          alertCards={alertCards}
          activeAlertFilters={activeAlertFilters}
          onAlertFilterChange={handleAlertFilterChange}
          customerOptions={customerOptions}
          selectedCustId={selectedCustId}
          onCustomerChange={handleCustomerChange}
          orgFilter={{ ...orgFilter, setLevel: handleOrgLevelChange }}
          isLoadingAlerts={isLoadingAlerts}
          shipmentStatus={shipmentStatus}
          shipmentStatusCounts={shipmentStatusCounts}
          onShipmentStatusChange={handleShipmentStatusChange}
          loadType={loadType}
          loadTypeCounts={loadTypeCounts}
          onLoadTypeChange={handleLoadTypeChange}
          geocodingErrorsCount={geocodingErrorsCount}
          onGeocodingErrorsClick={() => setShowGeocodingErrorsModal(true)}
        />
      </div>

      <GeocodingErrorsModal
        isOpen={showGeocodingErrorsModal}
        onClose={() => setShowGeocodingErrorsModal(false)}
        onSuccess={handleGeocodingErrorsSuccess}
      />
    </div>
  );
};

export default InTransitShipmentsPage;
